/* eslint-disable no-redeclare */
import {
  buildEnumCSV,
  City,
  County,
  CountyFips,
  Decimal,
  DomainSessionId,
  DomainUserId,
  Email,
  FirstName,
  FPL,
  Gender,
  HealthcareSessionId,
  HealthcareUserId,
  HouseholdIncome,
  HouseholdSize,
  Integer,
  IpAddress,
  LastName,
  MaritalStatus,
  NonEmptyText,
  Source,
  SubProductType,
  TrueFalse,
  Url,
  USAStateAcronym,
  UUID,
  YesNo,
  ZipCode,
} from '@hc-frontend/core-types';
import { z } from 'zod';

import {
  EstimatedSpendingDto,
  ImmediateCoverageDto,
  KindOfMedicarePlanDto,
} from '../../../dtos';

export const BLANK_DTO_VALUE = '';
export const BLANKDto = z.literal(BLANK_DTO_VALUE);

const DateText = z.string();

export const FamilyMemberDto = z.object({
  dateOfBirth: DateText.optional(),
  firstName: FirstName.optional(),
  lastName: LastName.optional(),
  gender: Gender.optional(),
  tobaccoUser: z.boolean().optional(),
});

export type FamilyMemberDto = z.infer<typeof FamilyMemberDto>;

export const FamilyChild = FamilyMemberDto.extend({
  journeyMemberId: z.enum(['Child_1', 'Child_2', 'Child_3', 'Child_4']),
  relationship: z.literal('CHILD'),
});
export type FamilyChild = z.infer<typeof FamilyChild>;

export const FamilySpouse = FamilyMemberDto.extend({
  journeyMemberId: z.literal('Spouse'),
  relationship: z.literal('SPOUSE'),
});
export type FamilySpouse = z.infer<typeof FamilySpouse>;

export const FamilyDto = FamilyChild.or(FamilySpouse);
export type FamilyDto = z.infer<typeof FamilyDto>;

export const WhenYouNeedMedicareCoverageDto = z.enum([
  'right_away',
  'next_month_or_later',
  'not_sure',
]);

export const WhenYouNeedMedicareCoverageOrBlankDto = z.union([
  WhenYouNeedMedicareCoverageDto,
  BLANKDto,
]);

export type WhenYouNeedMedicareCoverageOrBlankDto = z.infer<
  typeof WhenYouNeedMedicareCoverageOrBlankDto
>;

export const EnrolledPartAPartBDto = z.enum(['yes', 'no', 'not_sure']);

export const UpdateInsureWhoDto = z.enum(['Just me', 'Myself and others']);

export type UpdateInsureWhoDto = z.infer<typeof UpdateInsureWhoDto>;

export const VisitPurposeDto = z.enum([
  'eligibility_check',
  'current_plan_assistance',
  'switch_plan',
  'first_time_enrollment',
  'other',
]);

export type VisitPurposeDto = z.infer<typeof VisitPurposeDto>;

export const InsuranceOptionsDto = z.enum([
  'term',
  'permanent',
  'final_expense',
  'not_sure',
]);

export type InsuranceOptionsDto = z.infer<typeof InsuranceOptionsDto>;

export const QualifyingLifeEventDto = z.enum([
  'lost-coverage',
  'got-new-job',
  'moved-new-state',
  'had-a-baby',
  'none-of-these',
]);

export type QualifyingLifeEventDto = z.infer<typeof QualifyingLifeEventDto>;

export const UpdateCoverageDurationDto = z.enum([
  'Permanent',
  'Temporary',
  'Not sure',
]);

export type UpdateCoverageDurationDto = z.infer<
  typeof UpdateCoverageDurationDto
>;

export const UpdateSeniorCheckDto = z.enum(['O65', 'U65']);

export type UpdateSeniorCheckDto = z.infer<typeof UpdateSeniorCheckDto>;

export const EnrolledPartAPartBOrBlankDto = z.union([
  EnrolledPartAPartBDto,
  BLANKDto,
]);
export type EnrolledPartAPartBOrBlankDto = z.infer<
  typeof EnrolledPartAPartBOrBlankDto
>;

export const KindOfMedicarePlanOrBlankDto = z.union([
  KindOfMedicarePlanDto,
  BLANKDto,
]);
export type KindOfMedicarePlanOrBlankDto = z.infer<
  typeof KindOfMedicarePlanOrBlankDto
>;

export const ImmediateCoverageOrBlankDto = z.union([
  ImmediateCoverageDto,
  BLANKDto,
]);
export type ImmediateCoverageOrBlankDto = z.infer<
  typeof ImmediateCoverageOrBlankDto
>;

export const IncomeDto = z.enum(['below', 'between', 'over']);
export type IncomeDto = z.infer<typeof IncomeDto>;
export const IncomeOrBlankDto = z.union([IncomeDto, BLANKDto]);
export type IncomeOrBlankDto = z.infer<typeof IncomeOrBlankDto>;

export const QuestionAnswersDto = z.object({
  when_you_need_medicare_coverage: WhenYouNeedMedicareCoverageOrBlankDto,
  enrolled_part_a_part_b: EnrolledPartAPartBOrBlankDto,
  kind_of_medicare_plan: KindOfMedicarePlanOrBlankDto,
  county_fips: CountyFips,
  medicaid_eligible: z.enum([
    'qualified',
    'unqualified',
    'no-medicaid-no-subsidy',
    'medicaid',
  ]),
  'is-oep': z.enum(['is-oep-no', 'is-oep-yes']),
  is_phoenix: TrueFalse,
  is_under_65: z.enum(['U65', 'O65']),
  askForZipCode: TrueFalse,
  age: Integer,
  tobacco: YesNo,
  immediate_coverage_q: ImmediateCoverageOrBlankDto,
  coverage_duration: UpdateCoverageDurationDto,
  estimated_spending: EstimatedSpendingDto,
  'subsidy-qualify': z.enum([
    'qualified',
    'unqualified',
    'medicaid',
    'no-medicaid-no-subsidy',
  ]),
  'subsidy-check': z.number(),
  'subsidy-value': z.number(),
  income: IncomeOrBlankDto,
  fpl: Decimal,
  'quality-life-event': QualifyingLifeEventDto,
  insure_who: UpdateInsureWhoDto,
  mobile: z.enum(['y', 'n']),
  visit_purpose: VisitPurposeDto,
  insurance_options: buildEnumCSV(InsuranceOptionsDto, BLANK_DTO_VALUE),
});

export type QuestionAnswersDto = z.infer<typeof QuestionAnswersDto>;

/**
 * @See https://github.com/healthcarecom/lead-storage-service/blob/main/src/main/java/com/healthcare/backend/leadstorageservice/lead/service/dto/UpsertProspectDto.java
 */
export const UpdateProspectBaseDto = z
  .object({
    sessionId: HealthcareSessionId,
    userId: HealthcareUserId,
    domainUserId: DomainUserId,
    domainSessionId: DomainSessionId,
    standardizedMaterialId: NonEmptyText,
    productTypeId: z.number().min(2).max(6),
    subProductType: SubProductType.optional(),
    src: Source,
    firstName: FirstName,
    lastName: LastName,
    dateOfBirth: DateText,
    state: USAStateAcronym,
    email: Email,
    maritalStatus: MaritalStatus,
    householdSize: HouseholdSize,
    gender: Gender,
    householdIncome: HouseholdIncome,
    address: z.string().optional(),
    city: City,
    county: County,
    countyFips: CountyFips,
    zip: ZipCode,
    landingUrl: Url,
    landingPage: Url,
    ipAddress: IpAddress,
    journeyId: Integer,
    fpl: FPL,
    tobaccoUser: z.boolean(),
    tcpaConsent: NonEmptyText,
    tcpaUserAgent: NonEmptyText,
    tcpaUniversalId: UUID,
    tcpaSite: NonEmptyText,
    tcpaSession: NonEmptyText,
    trustedFormUrl: Url,
    language: NonEmptyText,
    traffic: z.enum(['INTERNAL', 'EXTERNAL']),
    family: FamilyDto.array(),
    phoneNumber: z.string().regex(/^\d{10}$/),
    areaCode: z.string().regex(/^\d{3}$/),
    additionalQuestions: QuestionAnswersDto.partial(),
    facebookBrowserId: NonEmptyText,
    facebookClickId: NonEmptyText,
  })
  .partial()
  .required({
    sessionId: true,
    userId: true,
  });

export type UpdateProspectBaseDto = z.infer<typeof UpdateProspectBaseDto>;

export const UpdateProspectDto = UpdateProspectBaseDto.required({
  userId: true,
  sessionId: true,
});

export type UpdateProspectDto = z.infer<typeof UpdateProspectDto>;
